import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";

const MATCHES = "matches";
const ROOM_MATCHES = "roomMatches";

function roomMatchRef(roomId, matchId) {
  return doc(db, MATCHES, roomId, ROOM_MATCHES, matchId);
}

function roomMatchesCol(roomId) {
  return collection(db, MATCHES, roomId, ROOM_MATCHES);
}

/**
 * @returns {Promise<import('../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch | null>}
 */
export async function fetchRoomMatch(roomId, matchId) {
  const snap = await getDoc(roomMatchRef(roomId, matchId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** @returns {Promise<import('../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch[]>} */
export async function listRoomMatches(roomId) {
  if (!roomId) return [];
  const snap = await getDocs(roomMatchesCol(roomId));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
}

/** @returns {Promise<import('../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch | null>} */
export async function fetchActiveLiveRoomMatch(roomId) {
  if (!roomId) return null;
  const q = query(roomMatchesCol(roomId), where("status", "==", "live"));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

export async function createRoomMatch(roomId, matchId, payload) {
  const ref = roomMatchRef(roomId, matchId);
  await setDoc(ref, {
    ...payload,
    roomId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function patchRoomMatch(roomId, matchId, fields) {
  await updateDoc(roomMatchRef(roomId, matchId), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

/** @returns {import('firebase/firestore').Unsubscribe} */
export function subscribeRoomMatches(roomId, onData) {
  if (!roomId) {
    onData([]);
    return () => {};
  }
  return onSnapshot(
    roomMatchesCol(roomId),
    { includeMetadataChanges: false },
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
      onData(rows);
    },
    (err) => {
      console.warn("[roomMatches] subscription error", err?.code || err);
    }
  );
}

export { ROOM_MATCHES as ROOM_MATCHES_SUBCOLLECTION };
